import React, { useEffect, useState, useRef, useContext } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, LogOutIcon, UserIcon, HomeIcon, BookOpenIcon, CalendarIcon, MessageSquareIcon, GraduationCapIcon, UsersIcon, ClockIcon, InfoIcon, SunIcon, MoonIcon, SearchIcon, SettingsIcon, ShieldIcon, CreditCardIcon, HelpCircleIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignInModalContext } from '../App';
import { NotificationCenter } from './NotificationCenter';
import { useTheme } from './providers/ThemeProvider';
export const Navbar = ({
  setIsDeleteAccountModalOpen
}) => {
  const {
    currentUser,
    userData,
    userType,
    logout
  } = useAuth();
  const {
    openTeacherLoginModal
  } = useContext(SignInModalContext);
  const {
    theme,
    setTheme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // ---- FIXED: Clean userType normalization ----
  const normalizedUserType = userType ? userType.toLowerCase().trim() : null;
  const isStudent = normalizedUserType === 'student';
  const isTeacher = normalizedUserType === 'teacher';
  // ---- FIXED: Single source of truth for dashboard routing ----
  let dashboardPath = '/';
  if (isStudent) {
    dashboardPath = '/student-dashboard';
  } else if (isTeacher) {
    dashboardPath = '/teacher-dashboard';
  }
  // ---- FIXED: Single source of truth for profile routing ----
  let profilePath = '/';
  if (isStudent) {
    profilePath = '/my-profile';
  } else if (isTeacher) {
    profilePath = '/teacher-profile';
  }
  // ---- FIXED: Single source of truth for calendar routing ----
  let calendarPath = '/';
  if (isStudent) {
    calendarPath = '/student-schedule';
  } else if (isTeacher) {
    calendarPath = '/teacher/calendar';
  }
  // ---- FIXED: Single source of truth for messages routing ----
  let messagesPath = '/';
  if (isStudent) {
    messagesPath = '/messages';
  } else if (isTeacher) {
    messagesPath = '/teacher-messages';
  }
  // Toggle theme
  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // ---- FIXED: Dedicated Dashboard click handler ----
  const handleDashboardClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (!dashboardPath || dashboardPath === '/') {
      // Authenticated but no role → safe fallback to student dashboard
      navigate('/student-dashboard');
      return;
    }
    // Navigate to the correct dashboard based on userType
    navigate(dashboardPath);
    window.scrollTo(0, 0);
  };
  // ---- FIXED: Dedicated Profile click handler ----
  const handleProfileClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(profilePath); // student profile route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(profilePath); // teacher profile route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Calendar click handler ----
  const handleCalendarClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(calendarPath); // student calendar/schedule route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(calendarPath); // teacher calendar route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Messages click handler ----
  const handleMessagesClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(messagesPath); // student messages route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(messagesPath); // teacher messages route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // Check if user is logged in based on Firebase auth
  useEffect(() => {
    const checkUserLoggedIn = () => {
      // STEP 1 FIX: Use Firebase currentUser as the SOLE source of truth
      // Removed the isOnDashboardPages() fallback that caused desync on cross-tab logout
      if (currentUser) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
      // Set loading to false after checking
      setIsLoading(false);
    };
    checkUserLoggedIn();
  }, [currentUser, location.pathname]);
  // Check if current location is a dashboard/logged-in page
  const isOnDashboardPages = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/messages' || location.pathname === '/student-schedule' || location.pathname === '/online-courses' || location.pathname === '/find-teachers' || location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/my-students' || location.pathname === '/my-availability' || location.pathname.startsWith('/teacher/') || isOnMyAccountPage();
  };
  // Check if current location is a My Account page
  const isOnMyAccountPage = () => {
    return location.pathname === '/my-info' || location.pathname === '/settings' || location.pathname === '/account-security' || location.pathname === '/billing-payouts' || location.pathname === '/help-support';
  };
  // Check if current location is a teacher dashboard page
  const isOnTeacherDashboardPage = () => {
    return location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/teacher-classes' || location.pathname === '/reports' || location.pathname === '/my-availability' || location.pathname === '/my-students' || location.pathname === '/messages' || location.pathname === '/teacher-messages' || location.pathname === '/assigned-tasks' || location.pathname === '/teacher-sheet-music' || location.pathname === '/teacher/calendar2' || location.pathname.startsWith('/student-profile/') || isOnMyAccountPage();
  };
  // Check if current location is a student page
  const isOnStudentPage = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/find-teachers' || location.pathname === '/student-schedule' || location.pathname === '/messages' || location.pathname === '/online-courses' || location.pathname === '/sheet-music' || location.pathname === '/student-settings' || isOnMyAccountPage();
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside - only add this effect when dropdown is open
  useEffect(() => {
    if (!isAccountDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
    };
    // Add event listener with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountDropdownOpen]);
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Always prevent default navigation
    e.preventDefault();
    if (isLoggedIn) {
      // If logged in, navigate to dashboard (not landing page)
      if (dashboardPath && dashboardPath !== '/') {
        navigate(dashboardPath);
      } else {
        // Fallback if dashboardPath is not set
        navigate('/teacher-dashboard');
      }
      // Ensure we scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (location.pathname === '/') {
      // If we're already on the homepage and not logged in, just scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If on another page and not logged in, navigate to home
      // Store a flag in localStorage to indicate we should scroll to top after navigation
      localStorage.setItem('scrollToTop', 'true');
      navigate('/');
    }
  };
  const handleFindTeacherClick = () => {
    // Ensure we scroll to top when navigating to student signup
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  const handleNavLinkClick = (sectionId: string) => {
    // If logged in, don't navigate to homepage sections
    if (isLoggedIn) {
      return;
    }
    if (location.pathname === '/') {
      // If already on homepage, just scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      // If on another page, navigate to homepage first, then scroll to section
      // We'll use localStorage to remember which section to scroll to after navigation
      localStorage.setItem('scrollToSection', sectionId);
    }
    // Close the desktop menu if it's open
    setIsDesktopMenuOpen(false);
  };
  // New function to handle scroll to top for already active pages
  const handleSamePageNavigation = (path: string, e: React.MouseEvent) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Open teacher login modal directly (skip role selection for teacher-only beta)
  const handleOpenSignInModal = () => {
    openTeacherLoginModal();
    setIsMenuOpen(false); // Close mobile menu if open
    setIsDesktopMenuOpen(false); // Close desktop menu if open
  };
  // Check if we need to scroll to a section after navigation
  useEffect(() => {
    if (location.pathname === '/') {
      const sectionId = localStorage.getItem('scrollToSection');
      if (sectionId) {
        // Small delay to ensure the page has loaded
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }
          localStorage.removeItem('scrollToSection');
        }, 100);
      }
    }
  }, [location.pathname]);
  // Check if we need to scroll to top after navigation
  useEffect(() => {
    if (location.pathname === '/' && localStorage.getItem('scrollToTop') === 'true') {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
        localStorage.removeItem('scrollToTop');
      }, 100);
    }
  }, [location.pathname]);
  const handleLogout = async () => {
    try {
      // Use Firebase logout
      await logout();
      // Redirect to home page
      navigate('/');
      // Close the dropdown
      setIsAccountDropdownOpen(false);
      // Update login state
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  // Safe navigation function that maintains authentication state
  const handleAuthenticatedNavigation = (path: string) => {
    console.log('[MOBILE MENU] handleAuthenticatedNavigation called:', path);
    // Conditional navigation based on user type for specific paths
    if (path === '/messages' && isTeacher) {
      navigate('/teacher-messages');
    } else {
      navigate(path);
    }
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    // Close menu AFTER navigation using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after navigation');
      setIsMenuOpen(false);
      setIsAccountDropdownOpen(false);
    });
  };
  // Helper for menu actions: execute action first, then close menu
  const handleMenuAction = (action: () => void, actionName: string = 'action') => {
    console.log('[MOBILE MENU] handleMenuAction called:', actionName);
    // Execute the action first
    action();
    // Close menu after action completes using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after', actionName);
      setIsMenuOpen(false);
    });
  };
  // If still loading, render a simplified navbar to prevent errors
  if (isLoading) {
    return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-navy-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`} data-id="element-2390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2391">
          <div className="flex items-center justify-between h-16" data-id="element-2392">
            <div className="flex items-center pl-6 md:pl-12" data-id="element-2393">
              <Link to="/" className="text-2xl font-serif font-bold text-white flex items-center" data-id="element-2394">
                <span data-id="element-2395">MaestroConnect</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>;
  }
  return <nav className={`fixed top-0 left-0 right-0 z-50 ${location.pathname === '/' && !isLoggedIn ? 'bg-navy-800/90 backdrop-blur-md' : 'bg-navy-800/90 backdrop-blur-md'} shadow-lg`} data-id="element-2396">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2397">
        <div className="flex justify-between items-center h-20" data-id="element-2398">
          {/* Logo - Now aligned with main content */}
          <a href="/" className="flex items-center" onClick={handleLogoClick} data-id="element-2399">
            <div className="text-xl md:text-2xl font-serif font-bold text-white" data-id="element-2400">
              <span className="text-deep-pink" data-id="element-2401">Maestro</span>Connect
            </div>
          </a>

          <div className="md:hidden flex items-center gap-3" data-id="element-2402">
            {/* Notification bell for mobile - only show when logged in */}
            {isLoggedIn && <NotificationCenter darkMode={isDark} data-id="element-2403" />}

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="inline-flex items-center justify-center p-3 rounded-md text-gray-400 hover:text-white hover:bg-navy-800 focus:outline-none" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} style={{
            minWidth: '48px',
            minHeight: '48px'
          }} data-id="element-2404">
              {isMenuOpen ? <XIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2405" /> : <MenuIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2406" />}
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8 justify-center pr-12" data-id="element-2407">
            {isLoggedIn ?
          // Dashboard navigation for logged-in users
          <>
                {isStudent ?
            // Student Navigation - Simplified for all internal pages
            isOnStudentPage() ?
            // Simplified navigation for all student internal pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2408">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2409" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2410">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2411" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/find-teachers', e)} data-id="element-2412">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2413" />
                        Find Teachers
                      </Link>
                      <Link to="/messages" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/messages' ? 'text-deep-pink' : ''}`} onClick={e => {
                // Force immediate scroll to top when navigating to messages
                // Use setTimeout to ensure this happens after navigation
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                  });
                }, 0);
              }} data-id="element-2414">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2415" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2416">
                        <NotificationCenter darkMode={isDark} data-id="element-2417" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages */}
                      {isStudent && isOnStudentPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2418">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2419" /> : <SunIcon className="h-5 w-5" data-id="element-2420" />}
                        </button>}
                    </> : location.pathname === '/' ?
            // Simplified navigation for student on landing page
            <>
                      <button onClick={handleDashboardClick} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2421">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2422" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2423">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2424" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2425">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2426" />
                        Find Teachers
                      </Link>
                      <Link to="/messages" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2427">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2428" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2429">
                        <NotificationCenter darkMode={isDark} data-id="element-2430" />
                      </div>
                      {/* Add dark/light mode toggle for My Account pages */}
                      {isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2431">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2432" /> : <SunIcon className="h-5 w-5" data-id="element-2433" />}
                        </button>}
                    </> :
            // Regular navigation for other pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2434">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2435" />
                        Dashboard
                      </button>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2436">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2437" />
                        Find Teachers
                      </Link>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2438">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2439" />
                        My Schedule
                      </Link>
                      <Link to="/online-courses" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/online-courses' ? 'text-deep-pink' : ''}`} data-id="element-2440">
                        <GraduationCapIcon className="h-4 w-4 mr-1" data-id="element-2441" />
                        Online Courses
                      </Link>
                      <Link to="/messages" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/messages' ? 'text-deep-pink' : ''}`} onClick={e => {
                // Force immediate scroll to top when navigating to messages
                // Use setTimeout to ensure this happens after navigation
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                  });
                }, 0);
              }} data-id="element-2442">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2443" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2444">
                        <NotificationCenter darkMode={isDark} data-id="element-2445" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages and My Account pages */}
                      {isStudent && isOnStudentPage() || isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2446">
                            {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2447" /> : <SunIcon className="h-5 w-5" data-id="element-2448" />}
                          </button>}
                    </> :
            // Teacher Navigation
            <>
                    <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2449">
                      <HomeIcon className="h-4 w-4 mr-1" data-id="element-2450" />
                      Dashboard
                    </button>
                    <button onClick={handleProfileClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === profilePath ? 'text-deep-pink' : ''}`} data-id="element-2451">
                      <UserIcon className="h-4 w-4 mr-1" data-id="element-2452" />
                      Teacher Page
                    </button>
                    <button onClick={handleCalendarClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === calendarPath ? 'text-deep-pink' : ''}`} data-id="element-2453">
                      <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2454" />
                      Calendar
                    </button>
                    {/* TEMPORARILY HIDDEN: Messages feature not ready for teachers */}
                    {/* <button
                     onClick={handleMessagesClick}
                     className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === messagesPath ? 'text-deep-pink' : ''}`}
                     >
                     <MessageSquareIcon className="h-4 w-4 mr-1" />
                     Messages
                     </button> */}
                    {/* Add NotificationCenter before dark/light mode toggle */}
                    <div className="px-2 py-1" data-id="element-2455">
                      <NotificationCenter darkMode={isDark} data-id="element-2456" />
                    </div>
                    {/* Dark/Light Mode Toggle — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2457">
                        {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2458" /> : <SunIcon className="h-5 w-5" data-id="element-2459" />}
                      </button>}
                  </>}

                {/* Account Dropdown for both user types */}
                <div className="relative account-dropdown" ref={accountDropdownRef} data-id="element-2460">
                  <button onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)} className="bg-[#3B82F6] border-2 border-[#3B82F6] hover:bg-[#2563EB] text-white text-sm py-1 px-3 rounded-lg transition-colors flex items-center" data-id="element-2461">
                    My Account
                    <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-2462" />
                  </button>
                  {isAccountDropdownOpen && <div className={`absolute right-0 mt-2 w-56 ${isDark ? 'bg-navy-800' : 'bg-white'} rounded-lg shadow-lg py-1 z-50`} data-id="element-2463">
                      {/* PHASE 1 FIX: Role-aware dropdown */}
                      {isStudent ?
                // STUDENT MENU: Only My Profile and Logout
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-profile')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-profile' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2464">
                            <UserIcon className="h-4 w-4 mr-2" data-id="element-2465" />
                            My Profile
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2466"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2467">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2468" />
                            Logout
                          </button>
                        </> : isTeacher ?
                // TEACHER MENU: Full account management
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-info')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-info' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2469">
                            <InfoIcon className="h-4 w-4 mr-2" data-id="element-2470" />
                            My Info
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/settings')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/settings' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2471">
                            <SettingsIcon className="h-4 w-4 mr-2" data-id="element-2472" />
                            Settings
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/account-security')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/account-security' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2473">
                            <ShieldIcon className="h-4 w-4 mr-2" data-id="element-2474" />
                            Account Security
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/billing-payouts')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/billing-payouts' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2475">
                            <CreditCardIcon className="h-4 w-4 mr-2" data-id="element-2476" />
                            Billing
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/help-support')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/help-support' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2477">
                            <HelpCircleIcon className="h-4 w-4 mr-2" data-id="element-2478" />
                            Help & Support
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2479"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2480">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2481" />
                            Logout
                          </button>
                        </> :
                // STEP 2 FIX: Fallback when userType is null but user appears logged in
                // Ensures logout is always accessible even in desync edge cases
                <>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2482">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2483" />
                            Logout
                          </button>
                        </>}
                    </div>}
                </div>
              </> : location.pathname === '/' ?
          // Public site navigation - homepage
          <>
                <a href="#how-it-works" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={e => {
              e.preventDefault();
              handleNavLinkClick('how-it-works');
            }} data-id="element-2484">
                  How It Works
                </a>
                <a href="#benefits" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={e => {
              e.preventDefault();
              handleNavLinkClick('benefits');
            }} data-id="element-2485">
                  Benefits
                </a>
                <a href="#pricing" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={e => {
              e.preventDefault();
              handleNavLinkClick('pricing');
            }} data-id="element-2486">
                  Pricing
                </a>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <a href="#tutorial-video" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={e => {
              e.preventDefault();
              handleNavLinkClick('tutorial-video');
            }} data-id="element-2487">
                  Tutorial
                </a>
                <a href="#contact" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={e => {
              e.preventDefault();
              handleNavLinkClick('contact');
            }} data-id="element-2488">
                  Contact
                </a>

                <button onClick={handleOpenSignInModal} className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors" data-id="element-2489">
                  Sign In
                </button>
              </> :
          // Public site navigation - other pages
          <>
                <Link to="/" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={() => {
              handleNavLinkClick('how-it-works');
            }} data-id="element-2490">
                  How It Works
                </Link>
                <Link to="/" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={() => {
              handleNavLinkClick('benefits');
            }} data-id="element-2491">
                  Benefits
                </Link>
                <Link to="/" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={() => {
              handleNavLinkClick('pricing');
            }} data-id="element-2492">
                  Pricing
                </Link>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <Link to="/" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" onClick={() => {
              handleNavLinkClick('contact');
            }} data-id="element-2493">
                  Contact
                </Link>
                <button onClick={handleOpenSignInModal} className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors" data-id="element-2494">
                  Sign In
                </button>
              </>}
          </div>
        </div>
      </div>

      {/* Mobile menu - improved styling and touch targets */}
      {isMenuOpen && <div className={`md:hidden ${isDark ? 'bg-navy-800' : 'bg-white'} relative z-20`} ref={mobileMenuRef} onPointerDown={e => {
      // Stop propagation to prevent overlay from catching events
      e.stopPropagation();
      console.log('[MOBILE MENU] Menu container pointer down - propagation stopped');
    }} data-id="element-2495">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3" data-id="element-2496">
            {isLoggedIn ?
        // Dashboard mobile navigation for logged-in users
        <>
                {isStudent ?
          // Student mobile navigation - improved touch targets
          <>
                    <button onClick={() => {
              handleDashboardClick();
              setIsMenuOpen(false);
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2497">
                      <div className="flex items-center" data-id="element-2498">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2499" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/find-teachers')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/find-teachers' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2500">
                      <div className="flex items-center" data-id="element-2501">
                        <SearchIcon className="h-5 w-5 mr-3" data-id="element-2502" />
                        Find Teachers
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/student-schedule')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/student-schedule' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2503">
                      <div className="flex items-center" data-id="element-2504">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2505" />
                        My Schedule
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/online-courses')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/online-courses' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2506">
                      <div className="flex items-center" data-id="element-2507">
                        <GraduationCapIcon className="h-5 w-5 mr-3" data-id="element-2508" />
                        Online Courses
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/messages')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/messages' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2509">
                      <div className="flex items-center" data-id="element-2510">
                        <MessageSquareIcon className="h-5 w-5 mr-3" data-id="element-2511" />
                        Messages
                      </div>
                    </button>
                    {/* Dark/Light Mode Toggle for mobile - Add for student pages and My Account pages */}
                    {isStudent && isOnStudentPage() || isOnMyAccountPage() && <button onClick={toggleTheme} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2512">
                          {isDark ? <>
                              <MoonIcon className="h-5 w-5 mr-3" data-id="element-2513" />
                              Dark Mode
                            </> : <>
                              <SunIcon className="h-5 w-5 mr-3" data-id="element-2514" />
                              Light Mode
                            </>}
                        </button>}
                  </> :
          // Teacher mobile navigation - Reordered links and improved touch targets
          <>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Dashboard button clicked');
              handleMenuAction(handleDashboardClick, 'Dashboard navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2515">
                      <div className="flex items-center" data-id="element-2516">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2517" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] My Profile button clicked');
              handleMenuAction(handleProfileClick, 'Profile navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === profilePath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2518">
                      <div className="flex items-center" data-id="element-2519">
                        <UserIcon className="h-5 w-5 mr-3" data-id="element-2520" />
                        Teacher Page
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Calendar button clicked');
              handleMenuAction(handleCalendarClick, 'Calendar navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === calendarPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2521">
                      <div className="flex items-center" data-id="element-2522">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2523" />
                        Calendar
                      </div>
                    </button>
                    {/* TEMPORARILY HIDDEN: Messages feature not ready for teachers */}
                    {/* <button
                      onClick={() => {
                        console.log('[MOBILE MENU] Messages button clicked')
                        handleAuthenticatedNavigation('/messages')
                      }}
                      className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/messages' || location.pathname === '/teacher-messages' ? (isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900') : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                     >
                      <div className="flex items-center">
                        <MessageSquareIcon className="h-5 w-5 mr-3" />
                        Messages
                      </div>
                     </button> */}
                    {/* Dark/Light Mode Toggle for mobile — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={() => {
              console.log('[MOBILE MENU] Theme toggle clicked');
              handleMenuAction(toggleTheme, 'Theme toggle');
            }} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2524">
                        {isDark ? <>
                            <MoonIcon className="h-5 w-5 mr-3" data-id="element-2525" />
                            Dark Mode
                          </> : <>
                            <SunIcon className="h-5 w-5 mr-3" data-id="element-2526" />
                            Light Mode
                          </>}
                      </button>}
                  </>}

                {/* Account dropdown items for mobile - PHASE 1 FIX: Role-aware */}
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-2 pt-2`} data-id="element-2527">
                  {isStudent ?
            // STUDENT MOBILE MENU: Only My Profile
            <button onClick={() => {
              console.log('[MOBILE MENU] My Profile (account) clicked');
              handleAuthenticatedNavigation('/my-profile');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-profile' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2528">
                      <div className="flex items-center" data-id="element-2529">
                        <UserIcon className="h-5 w-5 mr-3" data-id="element-2530" />
                        My Profile
                      </div>
                    </button> : isTeacher ?
            // TEACHER MOBILE MENU: Full account management
            <>
                      <button onClick={() => {
                console.log('[MOBILE MENU] My Info clicked');
                handleAuthenticatedNavigation('/my-info');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-info' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2531">
                        <div className="flex items-center" data-id="element-2532">
                          <InfoIcon className="h-5 w-5 mr-3" data-id="element-2533" />
                          My Info
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Settings clicked');
                handleAuthenticatedNavigation('/settings');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/settings' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2534">
                        <div className="flex items-center" data-id="element-2535">
                          <SettingsIcon className="h-5 w-5 mr-3" data-id="element-2536" />
                          Settings
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Account Security clicked');
                handleAuthenticatedNavigation('/account-security');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/account-security' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2537">
                        <div className="flex items-center" data-id="element-2538">
                          <ShieldIcon className="h-5 w-5 mr-3" data-id="element-2539" />
                          Account Security
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Billing clicked');
                handleAuthenticatedNavigation('/billing-payouts');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/billing-payouts' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2540">
                        <div className="flex items-center" data-id="element-2541">
                          <CreditCardIcon className="h-5 w-5 mr-3" data-id="element-2542" />
                          Billing
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Help & Support clicked');
                handleAuthenticatedNavigation('/help-support');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/help-support' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2543">
                        <div className="flex items-center" data-id="element-2544">
                          <HelpCircleIcon className="h-5 w-5 mr-3" data-id="element-2545" />
                          Help & Support
                        </div>
                      </button>
                    </> : null}
                </div>

                {/* Logout button for both user types — STEP 2 FIX: Always visible regardless of userType */}
                <button onClick={() => {
            console.log('[MOBILE MENU] Logout clicked');
            handleMenuAction(handleLogout, 'Logout');
          }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2546">
                  <div className="flex items-center" data-id="element-2547">
                    <LogOutIcon className="h-5 w-5 mr-3" data-id="element-2548" />
                    Logout
                  </div>
                </button>
              </> :
        // On homepage, use anchor links for mobile menu - improved touch targets
        <>
                {/* Sign In button moved to the top of the mobile menu */}
                <button className="bg-deep-pink hover:bg-burgundy text-white block w-full text-center px-4 py-4 rounded-md text-base font-medium" onClick={handleOpenSignInModal} data-id="element-2549">
                  Sign In
                </button>
                <Link to="/teacher-signup" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2550">
                  <div className="flex items-center" data-id="element-2551">
                    <UserIcon className="h-5 w-5 mr-3" data-id="element-2552" />
                    Become a Teacher
                  </div>
                </Link>
                <a href="#how-it-works" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={e => {
            e.preventDefault();
            setIsMenuOpen(false);
            handleNavLinkClick('how-it-works');
          }} data-id="element-2553">
                  How It Works
                </a>
                <a href="#benefits" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={e => {
            e.preventDefault();
            setIsMenuOpen(false);
            handleNavLinkClick('benefits');
          }} data-id="element-2554">
                  Benefits
                </a>
                <a href="#pricing" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={e => {
            e.preventDefault();
            setIsMenuOpen(false);
            handleNavLinkClick('pricing');
          }} data-id="element-2555">
                  Pricing
                </a>
                <a href="#tutorial-video" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={e => {
            e.preventDefault();
            setIsMenuOpen(false);
            handleNavLinkClick('tutorial-video');
          }} data-id="element-2556">
                  Tutorial
                </a>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                  to="/courses"
                  className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                  onClick={() => setIsMenuOpen(false)}
                 >
                  Courses
                 </Link> */}
                <a href="#contact" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={e => {
            e.preventDefault();
            setIsMenuOpen(false);
            handleNavLinkClick('contact');
          }} data-id="element-2557">
                  Contact
                </a>
              </>}
          </div>
        </div>}

      {/* Overlay for mobile when menu is open */}
      {isMenuOpen && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={() => {
      console.log('[MOBILE MENU] Overlay clicked - closing menu');
      setIsMenuOpen(false);
    }} onPointerDown={e => {
      console.log('[MOBILE MENU] Overlay pointer down');
    }} aria-hidden="true" data-id="element-2558"></div>}
    </nav>;
};